import { cn } from "@/lib/cn";

/**
 * Shimmer-loading placeholder. Three variants cover ~95% of cases:
 *
 *   <Skeleton.Block />   — generic rectangle
 *   <Skeleton.Text />    — text line(s)
 *   <Skeleton.Poster />  — 2:3 poster
 *   <Skeleton.Rows />    — table rows
 *   <Skeleton.Cards />   — card grid
 *
 * Page authors should prefer composing these into a layout that
 * matches the post-load shape, so the page doesn't reflow when data
 * arrives.
 */

function Block({
  className,
  width,
  height,
}: {
  className?: string;
  width?: string;
  height?: string;
}) {
  return (
    <div
      className={cn("skeleton", className)}
      style={{ width, height }}
      aria-hidden
    />
  );
}

function Text({
  lines = 1,
  width = "100%",
  className,
}: {
  lines?: number;
  width?: string;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="skeleton h-3"
          style={{
            width:
              i === lines - 1 && lines > 1 ? `calc(${width} * 0.65)` : width,
          }}
        />
      ))}
    </div>
  );
}

function Poster({ className }: { className?: string }) {
  return (
    <div className={cn("skeleton aspect-[2/3] w-full", className)} aria-hidden />
  );
}

function Rows({ count = 6 }: { count?: number }) {
  return (
    <div
      className="overflow-hidden rounded-md border border-line bg-surface/40"
      aria-hidden
    >
      <div className="border-b border-line bg-surface-2/50 px-3 py-2">
        <div className="skeleton h-3 w-24" />
      </div>
      <div className="divide-y divide-line">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-3">
            <div className="skeleton h-3 flex-1" />
            <div className="skeleton h-3 w-20" />
            <div className="skeleton h-3 w-24" />
            <div className="skeleton h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

function Cards({
  count = 6,
  cols = 4,
}: {
  count?: number;
  cols?: 2 | 3 | 4 | 5;
}) {
  const colsCls = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
    5: "grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
  }[cols];
  return (
    <div className={cn("grid gap-4", colsCls)} aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-md border border-line">
          <Skeleton.Poster />
          <div className="space-y-2 p-3">
            <div className="skeleton h-3 w-3/4" />
            <div className="skeleton h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export const Skeleton = { Block, Text, Poster, Rows, Cards };
